import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { EVARA, SERVICES, HETS_PHASES, INDUSTRIES } from "@/data/evara";

const PANELS = [
  {
    key: "about",
    label: "About",
    columns: [
      {
        header: "The Firm",
        links: [
          { label: "Who we are", to: "/about?section=who" },
          { label: "Mission & values", to: "/about?section=mission" },
          { label: "Why EVARA", to: "/about?section=why" },
          { label: "Leadership team", to: "/about?section=leadership" },
        ],
      },
      {
        header: "Get Started",
        links: [
          { label: "Operational Assessment", to: "/operational-assessment" },
          { label: "Book a consultation", to: "/book" },
          { label: "Contact us", to: "/contact" },
        ],
      },
    ],
    footer: { label: "More about EVARA", to: "/about" },
  },
  {
    key: "services",
    label: "Services",
    columns: [
      {
        header: "By Practice",
        links: SERVICES.slice(0, 4).map((s) => ({
          label: s.title,
          to: `/services?practice=${s.key}`,
        })),
      },
      {
        header: "By Practice",
        links: SERVICES.slice(4).map((s) => ({
          label: s.title,
          to: `/services?practice=${s.key}`,
        })),
      },
      {
        header: "Flagship Diagnostic",
        links: [
          { label: "Explore the Assessment", to: "/operational-assessment" },
          { label: "Schedule a Consultation", to: "/book" },
        ],
      },
    ],
    footer: { label: "View all services", to: "/services" },
  },
  {
    key: "framework",
    label: "Framework",
    columns: [
      {
        header: "The Six Steps",
        links: HETS_PHASES.slice(0, 3).map((p) => ({
          label: `${p.num}. ${p.title}`,
          to: `/framework?phase=${p.num}`,
        })),
      },
      {
        header: "\u00A0",
        links: HETS_PHASES.slice(3).map((p) => ({
          label: `${p.num}. ${p.title}`,
          to: `/framework?phase=${p.num}`,
        })),
      },
    ],
    footer: { label: "Explore the full framework", to: "/framework" },
  },
  {
    key: "industries",
    label: "Industries",
    columns: [
      {
        header: "By Sector",
        links: INDUSTRIES.slice(0, 3).map((ind) => ({
          label: ind.name,
          to: `/industries?sector=${ind.key}`,
        })),
      },
      {
        header: "\u00A0",
        links: INDUSTRIES.slice(3).map((ind) => ({
          label: ind.name,
          to: `/industries?sector=${ind.key}`,
        })),
      },
    ],
    footer: { label: "View all industries", to: "/industries" },
  },
];

function MegaPanel({ panel, onNavigate }) {
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
      data-testid={`mega-panel-${panel.key}`}
    >
      <div className="mega-panel w-[min(92vw,700px)]">
        <div className="grid gap-8 p-8" style={{ gridTemplateColumns: `repeat(${panel.columns.length}, minmax(0, 1fr))` }}>
          {panel.columns.map((col, ci) => (
            <div key={ci}>
              <div className="mega-col-header mb-3">{col.header}</div>
              <ul>
                {col.links.map((l) => (
                  <li key={l.label + l.to}>
                    <Link
                      to={l.to}
                      className="mega-link"
                      onClick={onNavigate}
                      data-testid={`mega-link-${panel.key}-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Link
          to={panel.footer.to}
          onClick={onNavigate}
          className="flex items-center gap-2 px-8 py-4 bg-[var(--evara-bg-soft)] border-t border-[var(--evara-border)] text-sm font-bold text-[var(--evara-text)] hover:text-[var(--evara-primary)] transition-colors"
          data-testid={`mega-footer-${panel.key}`}
        >
          {panel.footer.label} <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [openPanel, setOpenPanel] = React.useState(null);
  const [mobilePanel, setMobilePanel] = React.useState(null);
  const loc = useLocation();
  const navRef = React.useRef(null);

  React.useEffect(() => {
    setOpen(false);
    setOpenPanel(null);
  }, [loc.pathname, loc.search]);

  React.useEffect(() => {
    const onDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenPanel(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenPanel(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const closePanels = () => setOpenPanel(null);

  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="site-navbar" ref={navRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src={EVARA.logoUrl} alt="EVARA logo" className="h-11 w-11 md:h-12 md:w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-serif text-xl md:text-2xl tracking-tight" style={{ color: "var(--evara-primary)" }}>
              EVARA
            </div>
            <div className="hidden md:block text-[9px] tracking-[0.16em] uppercase text-[var(--evara-text-2)] mt-0.5">
              {EVARA.tagline}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          <NavLink
            to="/"
            data-testid="nav-home-link"
            className={({ isActive }) =>
              "px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors " +
              (isActive ? "text-[var(--evara-primary)]" : "text-[var(--evara-text)] hover:text-[var(--evara-primary)] hover:bg-[var(--evara-bg-soft)]")
            }
          >
            Home
          </NavLink>

          {PANELS.map((p) => {
            const isOpen = openPanel === p.key;
            return (
              <div key={p.key} className="relative">
                <button
                  onClick={() => setOpenPanel(isOpen ? null : p.key)}
                  className={
                    "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors " +
                    (isOpen
                      ? "text-[var(--evara-primary)] bg-[var(--evara-bg-soft)]"
                      : "text-[var(--evara-text)] hover:text-[var(--evara-primary)] hover:bg-[var(--evara-bg-soft)]")
                  }
                  aria-expanded={isOpen}
                  data-testid={`nav-${p.key}-trigger`}
                >
                  {p.label}
                  <ChevronDown
                    size={14}
                    className={"transition-transform duration-200 " + (isOpen ? "rotate-180" : "")}
                  />
                </button>
                {isOpen && <MegaPanel panel={p} onNavigate={closePanels} />}
              </div>
            );
          })}

          <NavLink
            to="/contact"
            data-testid="nav-contact-link"
            className={({ isActive }) =>
              "px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors " +
              (isActive ? "text-[var(--evara-primary)]" : "text-[var(--evara-text)] hover:text-[var(--evara-primary)] hover:bg-[var(--evara-bg-soft)]")
            }
          >
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:block">
          <Link to="/book" className="btn-primary" data-testid="nav-book-consultation-btn">
            Schedule a Consultation
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded-full border border-[var(--evara-border)]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--evara-border)] bg-white max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-1">
            <NavLink to="/" className="py-2.5 text-sm font-semibold" data-testid="mobile-nav-home-link">
              Home
            </NavLink>
            {PANELS.map((p) => {
              const expanded = mobilePanel === p.key;
              return (
                <div key={p.key} className="border-b border-[var(--evara-border)] last:border-0">
                  <button
                    onClick={() => setMobilePanel(expanded ? null : p.key)}
                    className="w-full flex items-center justify-between py-2.5 text-sm font-semibold"
                    data-testid={`mobile-nav-${p.key}-trigger`}
                  >
                    {p.label}
                    <ChevronDown size={15} className={"transition-transform " + (expanded ? "rotate-180" : "")} />
                  </button>
                  {expanded && (
                    <div className="pb-3 pl-3 flex flex-col">
                      {p.columns.flatMap((c) => c.links).map((l) => (
                        <Link key={l.label + l.to} to={l.to} className="py-2 text-sm text-[var(--evara-text-2)]">
                          {l.label}
                        </Link>
                      ))}
                      <Link to={p.footer.to} className="py-2 text-sm font-bold text-[var(--evara-primary)]">
                        {p.footer.label} →
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
            <NavLink to="/contact" className="py-2.5 text-sm font-semibold" data-testid="mobile-nav-contact-link">
              Contact
            </NavLink>
            <Link to="/book" className="btn-primary mt-3 justify-center" data-testid="mobile-nav-book-btn">
              Schedule a Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
